import { Component, Inject, OnInit } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { AssociateItemsDialogData } from '@components/associate-items-dialog/1.0';
import { BadgeType } from '@constants/badge/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import { BossItemMapping } from '@interfaces/boss/1.0';
import { BossService } from '@services/boss/boss.service';
import { MessageType } from '@constants/message/1.0';
import { minimumFormItemsValidator } from '@helpers/Validators';
import { IsEqualService } from '@services/utilities/1.0/is-equal.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { ItemListComponent } from '../../item-list/1.0/item-list.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { NumbersOnlyDirective } from '../../../directives/numbers-only/1.0/numbers-only.directive';

@Component({
  selector: 'txp-associate-items-dialog',
  templateUrl: './associate-items-dialog.component.html',
  styleUrls: ['./associate-items-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    MessageComponent,
    ItemListComponent,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    CdkDropList,
    NgFor,
    CdkDrag,
    CdkDragHandle,
    MatSlideToggle,
    NumbersOnlyDirective,
    CdkDragPlaceholder,
    MatDialogActions,
    TranslateModule,
  ],
})
export class AssociateItemsDialogComponent implements OnInit {
  badgeType = BadgeType; // expose enum to template
  buttonStyle = ButtonStyle;
  noneSelectedMsg: string;
  filterText: string;
  formSubmitted = false;
  isLoading = false;
  messageType = MessageType;
  itemAlreadyExistsError = false;
  showError = false;
  form: UntypedFormGroup;
  entitiesSelectedText: string;
  selectedItems: BossItemMapping[] = [];
  stagedSelectedItems: BossItemMapping[];
  itemsList: BossItemMapping[];

  constructor(
    private bossService: BossService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AssociateItemsDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: AssociateItemsDialogData,
  ) {
    // copy the array because we will be manipulating it
    if (data.substitutionMode) {
      const itemsMap = this.data.fullList.reduce((acc, item) => {
        acc[item.itemId] = item;
        return acc;
      }, {});

      this.selectedItems = Object.keys(data.item.substitutions).map((key) => ({
        ...itemsMap[key],
        ...data.item.substitutions[key],
      }));
    } else {
      this.selectedItems = data.selectedList.slice();
    }

    this.itemsList = this.removeFromList(this.selectedItems, data.fullList);

    if (data.substitutionMode) {
      // removing other products that are added in the combo + the current product substitutions
      const otherProductsMap = this.data.selectedList.reduce((obj, item) => {
        obj[item.itemId] = item;
        return obj;
      }, {});

      this.itemsList = this.itemsList.filter(
        (item) => !otherProductsMap[item.itemId] && !data.item.substitutions[item.itemId],
      );
    }

    // setup translations
    this.entitiesSelectedText = `${data.translations.type.toLowerCase()} ${translateService.instant(
      'menu.common.selected',
    )}`;
    this.noneSelectedMsg = translateService
      .instant('menu.common.noSelectedMsg')
      .replace(':items', data.translations.type);
    this.filterText = `${translateService.instant('menu.common.search')} ${data.translations.type}`;
    this.form = this.formBuilder.group({
      itemName: new UntypedFormControl(this.data.item.name),
      items: new UntypedFormGroup(
        {},
        data.item?.type.toUpperCase() === 'COMBO' ? [minimumFormItemsValidator(1)] : undefined,
      ),
    });
  }

  ngOnInit() {
    const itemsArray = this.form.get('items') as UntypedFormGroup;

    this.selectedItems?.forEach((item) => {
      const newRow = this.createRow(item);

      itemsArray.addControl(item.itemId, newRow);
    });
  }

  createRow(row: BossItemMapping) {
    const rowBody: any = {
      available: row.available || false,
      itemId: row.itemId,
      name: row.name,
      price: row.price,
      initialPrice: this.data.selectedStore ? row.storePrice : row.price,
      editing: true,
      substitutions: row.substitutions,
    };

    if (this.data.substitutionMode) {
      rowBody.surchargeUseAmount =
        row.surchargeUseAmount !== undefined ? row.surchargeUseAmount : '';
    }

    if (this.data.selectedStore) {
      Object.assign(rowBody, {
        available: row.available || false,
      });
    }

    return this.formBuilder.group(rowBody);
  }

  handleCancel() {
    if (this.data.substitutionMode) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.close();
  }

  createItemIfNeeded(): Observable<any> {
    const newData = { ...this.data.item, name: this.form.value.itemName };

    if (newData.type.toUpperCase() === 'COMBO' && this.data.newCombo) {
      this.isLoading = true;
      return this.bossService.addItem(newData);
    }

    return of({});
  }

  handleAssociations() {
    const obs: Observable<any>[] = [];
    const initialListMap = this.data.selectedList.reduce((acc, item) => {
      acc[item.itemId] = {
        ...item,
        keep: false,
      };
      return acc;
    }, {});

    this.selectedItems.forEach((item) => {
      const itemControl = this.form.get('items').get(item.itemId);
      const shouldInherit = !itemControl.get('editing').value;
      const oldItem = initialListMap[item.itemId];
      const isMainItem = ['product', 'combo'].includes(this.data.item.type.toLowerCase());
      const parentId = isMainItem ? this.data.item.itemId : item.itemId;
      let body;

      if (isMainItem) {
        body = {
          ...itemControl.value,
          price: parseFloat(itemControl.value.price),
        };
      } else {
        body = {
          ...this.data.item,
          price: parseFloat(itemControl.value.price),
        };

        if (this.data.selectedStore) {
          Object.assign(body, {
            available: itemControl.value.available,
          });
        }
      }

      delete body.initialPrice;

      if (shouldInherit) {
        delete body.price;
      }

      if (oldItem) {
        const differentPrice = `${oldItem.price}` !== `${body.price}`;
        const differentAvailability = `${oldItem.available}` !== `${body.available}`;
        const differentSubstitutions = !IsEqualService.isEqual(
          oldItem.substitutions,
          body.substitutions,
        );

        // item will not be deleted
        oldItem.keep = true;

        if (differentPrice || differentAvailability || shouldInherit || differentSubstitutions) {
          // different price or changed to inherit
          // patch item
          if (this.data.selectedStore) {
            obs.push(
              this.bossService.updateStoreItemAssociation(this.data.selectedStore, parentId, body),
            );
          } else {
            obs.push(this.bossService.updateItemAssociation(parentId, body));
          }
        }
      } else {
        // push item
        if (this.data.selectedStore) {
          obs.push(
            this.bossService.createStoreItemAssociation(this.data.selectedStore, parentId, body),
          );
        } else {
          obs.push(this.bossService.createItemAssociation(parentId, body));
        }
      }
    });

    Object.keys(initialListMap).forEach((key) => {
      const oldItem = initialListMap[key];
      const isMainItem = ['product', 'combo'].includes(this.data.item.type.toLowerCase());
      const parentId = isMainItem ? this.data.item.itemId : key;
      const childId = isMainItem ? key : this.data.item.itemId;

      if (!oldItem.keep) {
        // delete item
        if (this.data.selectedStore) {
          obs.push(
            this.bossService.deleteStoreItemAssociation(this.data.selectedStore, parentId, childId),
          );
        } else {
          obs.push(this.bossService.deleteItemAssociation(parentId, childId));
        }
      }
    });

    this.isLoading = true;

    if (obs.length === 0) {
      this.isLoading = false;
      this.dialogRef.close();
      return;
    }

    forkJoin(obs).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.showError = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
    });
  }

  handleSubstitutions() {
    const itemsControl = this.form.get('items');

    return this.selectedItems.reduce((acc, item) => {
      const itemControl = itemsControl.get(item.itemId);
      const surchargeUseAmount = itemControl.get('surchargeUseAmount').value;

      acc[item.itemId] = {
        description: itemControl.get('name').value,
        sku: item.itemId,
        surchargeUseAmount: surchargeUseAmount ? parseFloat(surchargeUseAmount) : undefined,
      };

      return acc;
    }, {});
  }

  handleDone() {
    if (this.data.substitutionMode) {
      this.dialogRef.close(this.handleSubstitutions());
      return;
    }

    this.form.markAllAsTouched();
    this.formSubmitted = true;

    if (!this.form.valid) {
      return;
    }

    this.createItemIfNeeded().subscribe({
      next: this.handleAssociations.bind(this),
      error: (e) => {
        if (e.error === 'item already exists') {
          this.itemAlreadyExistsError = true;
        } else {
          this.showError = true;
        }

        this.isLoading = false;
      },
    });
  }

  onSelected(selected: BossItemMapping[]) {
    this.stagedSelectedItems = selected;
  }

  addSelected() {
    if (this.stagedSelectedItems) {
      const itemsFormGroup = this.form.get('items') as UntypedFormGroup;
      const shouldOverridePrice = this.data.item.type === 'MODIFIER';
      const priceKey = this.data.selectedStore ? 'storePrice' : 'price';

      this.stagedSelectedItems.forEach((item) => {
        const newRow = this.createRow({
          ...item,
          price: shouldOverridePrice ? this.data.item[priceKey] : item[priceKey],
        });

        if (this.data.selectedStore) {
          Object.assign(newRow, {
            available: true,
          });
        }

        itemsFormGroup.addControl(item.itemId, newRow);
      });

      this.selectedItems = this.selectedItems.concat(this.stagedSelectedItems);
      this.itemsList = this.removeFromList(this.stagedSelectedItems, this.itemsList);
      this.stagedSelectedItems = [];
    }
  }

  /**
   * Removes all items in removeList from masterList without changing masterList. Returns
   * a new array without the removed items
   *
   * @param removeList - array of items to remove
   * @param masterList - array of items to remove from
   */
  removeFromList(removeList: any[], masterList: any[]) {
    // don't modify the original array
    const newList = masterList.slice();
    for (const item of removeList) {
      const index = newList.indexOf(item);
      if (index > -1) {
        newList.splice(index, 1);
      }
    }
    newList.sort((a, b) => (a.name > b.name ? 1 : -1));
    return newList;
  }

  onItemDrop(event) {
    const previousIndex = this.selectedItems.findIndex((item) => item === event.item.data);

    moveItemInArray(this.selectedItems, previousIndex, event.currentIndex);
  }

  onEdit(item: BossItemMapping) {
    const itemControl = this.form.get('items').get(item.itemId);

    itemControl.patchValue({
      editing: true,
      price: itemControl.value.initialPrice,
    });
  }

  onChooseSubstitutes(item: BossItemMapping) {
    const itemsControl = this.form.get('items').get(item.itemId);
    const dialogData: AssociateItemsDialogData = {
      brandId: this.data.brandId,
      item: {
        ...item,
        ...itemsControl.value,
      },
      fullList: this.data.fullList,
      selectedList: this.data.selectedList,
      translations: {
        title: this.translateService.instant('boss.pos.associationModal.productSubstitutionTitle'),
        type: this.translateService.instant('boss.pos.associationModal.modifierList'),
        group: this.translateService.instant('boss.pos.associationModal.productName'),
        listTitle: this.translateService.instant('boss.pos.associationModal.modifierListList'),
      },
      substitutionMode: true,
    };
    const dialogRef = this.dialog.open(AssociateItemsDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const itemControl = this.form.get('items').get(item.itemId);

        itemControl.patchValue({
          substitutions: result,
        });
      }
    });
  }

  onRevert(item: BossItemMapping) {
    const itemControl = this.form.get('items').get(item.itemId);

    itemControl.patchValue({
      editing: false,
    });
  }

  handleDelete(item: BossItemMapping) {
    const itemsControl = this.form.get('items') as UntypedFormGroup;

    itemsControl.removeControl(item.itemId);

    this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
    // add back to the master list
    this.itemsList.push(item);
    // refresh reference so item-data component will detect changes
    this.itemsList = this.itemsList.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
  }
}
